import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import TicketAdminDialog from '@/components/admin/TicketAdminDialog';
import { PRIORITY_STYLES, TICKET_PRIORITIES, TICKET_STATUSES, TICKET_STATUS_STYLES } from '@/shared/constants';
import { TICKET_FIELDS } from '@/shared/fields';
import { idOf, paged } from '@/shared/data';
import { formatRelativeTime } from '@/utils/date';

const { eq, or, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/tickets', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'LifeBuoy', label: 'Tickets', section: 'Support', profiles: ['Admin_c', 'Staff_c'], order: 50 };

const PAGE_SIZE = 20;

export default function AdminTickets() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'all';
  const [priority, setPriority] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [status, priority, q]);

  function setStatus(value) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'all') next.delete('status');
        else next.set('status', value);
        return next;
      },
      { replace: true },
    );
  }

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('tickets_c').select(TICKET_FIELDS);
    if (status !== 'all') query = query.where(eq('status_c', status));
    if (priority !== 'all') query = query.where(eq('priority_c', priority));
    if (q) query = query.where(or(contains('Name', q), contains('email_c', q)));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [status, priority, q, page]);

  const list = data?.rows ?? [];
  const filtered = status !== 'all' || priority !== 'all' || !!q;

  function clear() {
    setSearch('');
    setPriority('all');
    setStatus('all');
  }

  const columns = [
    {
      label: 'Subject',
      render: (t) => (
        <RowLink to={`/admin/tickets/${t.Id}`} sub={t.email_c}>
          {t.Name}
        </RowLink>
      ),
    },
    {
      label: 'Order',
      className: 'hidden lg:table-cell',
      render: (t) =>
        idOf(t.order_c) ? (
          <RowLink to={`/admin/orders/${idOf(t.order_c)}`} mono>
            {t.order_c?.Name ?? 'View order'}
          </RowLink>
        ) : (
          <Dash />
        ),
    },
    { label: 'Category', className: 'hidden md:table-cell', render: (t) => t.category_c || <Dash /> },
    { label: 'Priority', className: 'hidden sm:table-cell', render: (t) => <StatusBadge value={t.priority_c} styles={PRIORITY_STYLES} /> },
    { label: 'Status', render: (t) => <StatusBadge value={t.status_c} styles={TICKET_STATUS_STYLES} /> },
    {
      label: 'Last activity',
      className: 'hidden md:table-cell text-right text-muted-foreground',
      render: (t) => formatRelativeTime(t.last_reply_at_c || t.CreatedOn),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Support"
        title="Tickets"
        description="Customer problems, oldest open ones first in your head, newest first on the list."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            Open ticket
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search subject or email…" />
        <FilterSelect value={status} onChange={setStatus} values={TICKET_STATUSES} allLabel="All statuses" />
        <FilterSelect value={priority} onChange={setPriority} values={TICKET_PRIORITIES} allLabel="All priorities" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'LifeBuoy',
          title: filtered ? 'No tickets match' : 'No tickets yet',
          description: filtered ? 'Nothing fits these filters.' : 'Customers open tickets from their account.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'Open ticket',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <TicketAdminDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
